import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  LineChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Line,
  ReferenceLine,
  Tooltip,
} from "recharts";
import api from "../../globals/axios";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ErrorOutline as ErrorOutlineIcon } from "@mui/icons-material";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { getSoilMoistureData } from "../../api/amSatelliteReport";
import AmSoilMoistureGraphSummary from "./AmSoilMoistureGraphSummary";

const GraphTooltip = ({
  active,
  payload,
  label,
  wiltingPoint,
  fieldCapacity,
  t,
}) => {
  if (active && isNotNullOrEmptyArray(payload)) {
    return (
      <Box
        sx={{
          backgroundColor: "white",
          border: "1px solid #ccc",
          padding: "10px",
          borderRadius: "4px",
        }}
      >
        <Typography variant="body2">
          {t("Date")}: {label}
        </Typography>
        <Typography variant="body2">
          {t("Field Soil Moisture (%)")}: {payload[0].value}%
        </Typography>
        <Typography variant="body2" color="#EB5353">
          {t("Minimum required")}: {wiltingPoint}%
        </Typography>
        <Typography variant="body2" color="#36AE7C">
          {t("Maximum advised")}: {fieldCapacity}%
        </Typography>
      </Box>
    );
  }

  return null;
};

const AMSoilMoistureGraph2 = ({ farmId, accessKey }) => {
  const [soilMoistureData, setSoilMoistureData] = useState([]);
  const [wiltingPoint, setWiltingPoint] = useState(0);
  const [fieldCapacity, setFieldCapacity] = useState(0);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    try {
      let data;
      if (accessKey) {
        data = await getSoilMoistureData(farmId, accessKey);
      } else {
        const response = await api.get(
          `/satellitedata/get-soil-moisture-insights-view/${farmId}`
        );
        data = response.data;
      }
      setSoilMoistureData(data?.data?.soil_moisture_trends || []);
      setWiltingPoint(data?.data?.wilting_point);
      setFieldCapacity(data?.data?.field_capacity);
      setIsError(false);
    } catch (error) {
      console.error("Error fetching Soil Moisture Data:", error);
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [farmId, accessKey]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          my: 4,
          py: 3,
        }}
      >
        <CircularProgress
          size={40}
          thickness={4}
          sx={{ mb: 2, color: "primary.main" }}
        />
        <Typography variant="h6" color="text.secondary">
          {t("Loading soil moisture data")}...
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t("This may take a moment")}
        </Typography>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box
        sx={{
          p: 3,
          my: 2,
          border: "1px solid #f5c6cb",
          borderRadius: 2,
          bgcolor: "#f8d7da",
          display: "flex",
          alignItems: "center",
        }}
      >
        <ErrorOutlineIcon sx={{ color: "#721c24", mr: 2, fontSize: 28 }} />
        <Box>
          <Typography
            variant="h6"
            sx={{ color: "#721c24", fontWeight: "bold", mb: 0.5 }}
          >
            {t("Unable to fetch soil moisture trend")}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <>
      {isNotNullOrEmptyArray(soilMoistureData) && (
        <>
          <AmSoilMoistureGraphSummary
            soilMoistureData={soilMoistureData}
            wiltingPoint={wiltingPoint}
            fieldCapacity={fieldCapacity}
          />
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={soilMoistureData}
              margin={{ top: 5, right: 30, bottom: 20, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                label={{
                  value: t("Date"),
                  position: "bottom",
                  offset: 0,
                }}
              />
              <YAxis
                domain={[0, 100]}
                label={{
                  value: t("Field Soil Moisture (%)"),
                  angle: -90,
                  position: "insideBottomLeft",
                }}
              />
              <Tooltip
                content={
                  <GraphTooltip
                    wiltingPoint={wiltingPoint}
                    fieldCapacity={fieldCapacity}
                    t={t}
                  />
                }
              />
              <Line
                type="monotone"
                dataKey="soil_moisture"
                stroke="#187498"
                name={`${t("Field Soil Moisture")} (%)`}
                activeDot={{ r: 8 }}
                isAnimationActive={!accessKey} // Disable animation if accessKey is present
              />
              <ReferenceLine
                y={wiltingPoint}
                stroke="#EB5353"
                label={{
                  value: t("Minimum required"),
                  position: "insideTopLeft",
                  fill: "#ff7f0e",
                }}
              />
              <ReferenceLine
                y={fieldCapacity}
                stroke="#36AE7C"
                label={{
                  value: t("Maximum advised"),
                  position: "insideTopLeft",
                  fill: "#2ca02c",
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </>
  );
};

export default AMSoilMoistureGraph2;
